@use "../../styles/breakpoint.module.scss" as *;
@use "../ProductCard/index.module" as productCard;

$grid-columns: 12;
$carousel-cols-with-content: 7;

.layout {
    composes: gridContainer from "../../styles/composable/layout.module.css";
    row-gap: var(--spacer-48);

    .carouselArea {
        grid-column: 1 / -1;
    }

    &:has([data-carousel-all-visible="true"]) {
        .progressBar {
            display: none;
        }

        .carouselArea {
            margin-right: 0;
        }

        .scrollContainer {
            text-align: center;
        }

        .slidesWrapper::after {
            display: none;
        }

        .slideItem:last-child {
            margin-right: 0;
        }

        .slideItem,
        .productSlideItem {
            text-align: initial;
        }
    }
}

.layoutWithRichText {
    composes: layout;

    .carouselArea {
        @media ($above-tablet) {
            grid-column: #{$grid-columns - $carousel-cols-with-content + 1} / -1;
        }
    }

    // On desktop, cards sit to the right of richtext, so left-align the cards
    // so it doesn't look weird
    @media ($above-tablet) {
        &:has([data-carousel-all-visible="true"]) {
            .scrollContainer {
                text-align: left;
            }

            .productSlidesWrapper {
                justify-content: left;
            }
        }
    }
}

.layoutWithoutRichText {
    composes: layout;
}

.richText {
    grid-column: 1 / span #{$grid-columns - $carousel-cols-with-content};

    @media ($above-tablet) {
        padding-right: var(--spacer-24);
    }

    @media ($below-tablet) {
        grid-column: 1 / -1;
        text-align: center;
        padding-bottom: var(--spacer-24);

        > * {
            margin-inline: auto;
        }
    }
}

.carouselArea {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-24);
    margin-right: calc(-1 * var(--labui-container-margin));
    overflow: hidden;
}

// Wraps every carousel's slides. Derives one slide's --width from how many
// slides are visible at each breakpoint plus the gutter between them. Lives on
// the wrapper so it inherits down to the slides, which just consume --width.
.slidesWrapper {
    --slides-visible: var(--slides-visible-desktop);
    --gutters: calc(var(--slides-visible) - 1);
    --gutter-width: var(--spacer-24);
    --gutter-space: calc(var(--gutter-width) * var(--gutters));
    --width: calc((100% - var(--gutter-space)) / var(--slides-visible));

    @media ($below-tablet) {
        --slides-visible: var(--slides-visible-tablet);
    }

    @media ($below-mobile) {
        --slides-visible: var(--slides-visible-mobile);
    }
}

.slideItem {
    width: var(--width);
    flex-shrink: 0;
    margin-right: var(--gutter-width);
}

// Trailing spacer, so the last card rests one container margin in at scroll end.
// It's a pseudo instead of margin because product slide grids don't work with
// the latter; this works for all cases.
.slidesWrapper::after {
    content: "";
    display: inline-block;
    width: calc(var(--labui-container-margin) - var(--gutter-width));
}

// Product slides reuse the full ProductCard, which aligns its rows across cards
// via subgrid. Subgrid needs a grid ancestor to set up the row tracks
.productSlidesWrapper {
    composes: slidesWrapper;
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: var(--width);
    grid-template-rows: repeat(productCard.$vertical-row-span, auto);
    // Firefox needs this defined here
    gap: var(--spacer-24) var(--gutter-width);
    // Center the cards when they don't fill the track (fewer products than fit).
    // The inline-block text-align centering used elsewhere doesn't reach grid
    // tracks; `safe` falls back to start when the cards overflow and scroll.
    justify-content: safe center;
}

.productSlideItem {
    display: grid;
    grid-template-rows: subgrid;
    grid-row: 1 / -1;
}

.arrows {
    inset: 0 var(--labui-container-margin) 0 0;
    z-index: 3;

    @media ($below-mobile) {
        display: none;
    }
}

.progressBar {
    grid-column: 1 / -1;
}

:export {
    gridColumns: $grid-columns;
    carouselColumnsWithContent: $carousel-cols-with-content;
}
